import { StyleSheet, TouchableOpacity, View } from "react-native";
import React, { useCallback, useMemo } from "react";
import { InvitationMode, ProfileType } from "../__generated__/graphql";
import { InvitationFieldsFragment } from "../__generated__/operations/graphql";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import VStack from "../ui_atoms/VStack";
import Icon from "./Icon";
import Text from "./Text";
import { SHORT_DATETIME_FORMAT_OPTIONS, useDateFormatter } from "../hooks/date";

type InvitationViewProps = {
    invitation: InvitationFieldsFragment;
    onSelectInvitation?: (invitation: InvitationFieldsFragment) => void | Promise<void>;
    disabled?: boolean;
    profileType?: ProfileType;
};

const InvitationView: React.FC<InvitationViewProps> = ({
    invitation,
    onSelectInvitation,
    disabled = false,
}) => {
    const styles = useStyleSheet();
    const { colors } = useTheme();
    const i18n = useI18n();
    const onPress = useCallback(async () => {
        if (onSelectInvitation) await onSelectInvitation(invitation);
    }, [invitation, onSelectInvitation]);
    const [Component, componentProps] = useMemo(() => {
        if (onSelectInvitation && !disabled) {
            return [TouchableOpacity, { onPress }];
        }
        return [View, {}];
    }, [onSelectInvitation, disabled, onPress]);
    const dateFormatter = useDateFormatter();

    return (
        // Component is either TouchableOpacity or View, but typescript does not know that
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <Component style={styles.cell} {...componentProps}>
            <View style={styles.cellInfo}>
                <Text style={styles.createdAt}>
                    {dateFormatter(new Date(invitation.createdAt), SHORT_DATETIME_FORMAT_OPTIONS)}
                </Text>
                {invitation.mode == InvitationMode.Receiver && (
                    <>
                        <Text style={styles.cellFrom}>
                            {i18n.t("invitation.from")} {invitation.senderName}
                        </Text>
                        <Text style={styles.cellCompany}>
                            <Icon name="company" size={14} color={colors.onSurfaceDisabled} />
                            &nbsp;
                            {invitation.clientName}
                        </Text>
                    </>
                )}
                {invitation.mode == InvitationMode.Sender && (
                    <>
                        <Text style={styles.cellFrom}>
                            {i18n.t("invitation.to", {
                                firstName: invitation.firstName,
                                lastName: invitation.lastName,
                                // An invitation sent by email (from the web app) has no phone number.
                                phoneNumber: invitation.phoneNumber || invitation.email || "",
                            })}
                        </Text>
                    </>
                )}
            </View>
            <View style={styles.actionButton}>
                <Icon name="chevron-right" size={24} color={colors.text} />
            </View>
        </Component>
    );
};

type InvitationListViewProps = {
    invitations: InvitationFieldsFragment[];
    onSelectInvitation?: (invitation: InvitationFieldsFragment) => void | Promise<void>;
    disabled?: boolean;
    profileType?: ProfileType;
};

export const InvitationListView: React.FC<InvitationListViewProps> = ({
    invitations,
    onSelectInvitation,
    disabled = false,
}) => {
    const { colors } = useTheme();

    return (
        <VStack
            alignment="stretch"
            gap={0.5}
            style={{ borderTopWidth: 1, borderColor: colors.accentLighter }}
        >
            {invitations.map((invitation) => {
                return (
                    <InvitationView
                        key={invitation.id}
                        invitation={invitation}
                        onSelectInvitation={onSelectInvitation}
                        disabled={disabled}
                    />
                );
            })}
        </VStack>
    );
};

const useStyleSheet = () => {
    const { gridSize, colors } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                cell: {
                    flex: 0,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    borderBottomWidth: 1,
                    backgroundColor: "white",
                    borderColor: colors.accentLighter,
                },
                cellInfo: {
                    flexShrink: 1,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    padding: gridSize,
                },
                cellFrom: {
                    flexShrink: 1,
                    flexWrap: "wrap",
                    fontWeight: "bold",
                    justifyContent: "center",
                    verticalAlign: "middle",
                },
                createdAt: {
                    flexShrink: 1,
                    flexWrap: "wrap",
                    fontSize: 10,
                    marginBottom: 5,
                    textTransform: "capitalize",
                },
                cellCompany: {
                    flexShrink: 1,
                    flexWrap: "wrap",
                },
                text: {
                    flexShrink: 1,
                    flexWrap: "wrap",
                    fontSize: 12,
                    marginTop: gridSize / 2,
                },
                actionButton: {
                    flexShrink: 0,
                    flexGrow: 0,
                    textAlign: "right",
                    padding: gridSize,
                },
            }),
        [gridSize, colors],
    );
};

export default InvitationListView;
